type RecordLike = Record<string, unknown>;

function toRecord(value: unknown): RecordLike | null {
    return value && typeof value === 'object' ? (value as RecordLike) : null;
}

function getByPath(root: unknown, path: string): unknown {
    const parts: Array<string | number> = [];
    String(path).replace(
        /([^[.\]]+)|\[(\d+)\]/g,
        (_, key: string, index: string) => {
            parts.push(index != null ? Number(index) : key);
            return '';
        }
    );

    let cur: unknown = root;
    for (const part of parts) {
        const currentRecord =
            cur && typeof cur === 'object'
                ? (cur as Record<string | number, unknown>)
                : null;
        if (!currentRecord) return undefined;
        cur = currentRecord[part];
    }
    return cur;
}

function normSet(arr: unknown = []): string[] {
    return Array.from(
        new Set(
            (Array.isArray(arr) ? arr : [])
                .map((value) =>
                    String(value || '')
                        .toLowerCase()
                        .trim()
                )
                .filter(Boolean)
        )
    );
}

export function normalizeAlignment(
    incoming: unknown,
    allowedArg?: readonly string[] | null
): string {
    const allowed = normSet(
        allowedArg && allowedArg.length
            ? allowedArg
            : ['left', 'center', 'right']
    );
    const fallback = allowed.includes('left') ? 'left' : allowed[0] || 'left';
    const normalizedInput = String(incoming ?? '')
        .toLowerCase()
        .trim();

    const map: Record<string, string> = {
        '': fallback,
        left: 'left',
        center: 'center',
        right: 'right',
    };

    const normalized = map[normalizedInput] ?? normalizedInput;
    return allowed.includes(normalized) ? normalized : fallback;
}

export function deepFindAlignmentKey(componentProps: unknown): string | null {
    const root = toRecord(componentProps);
    if (!root) return null;

    if (Object.prototype.hasOwnProperty.call(root, 'Alignment')) {
        return 'Alignment';
    }

    const collectionKeys = ['Files', 'Sections', 'Blocks', 'Items'];
    for (const collectionKey of collectionKeys) {
        const collection = root[collectionKey];
        if (Array.isArray(collection)) {
            const indices = collection
                .map((item, index) => {
                    const record = toRecord(item);
                    return record && 'Alignment' in record ? index : -1;
                })
                .filter((index) => index >= 0);

            if (indices.length === 1) {
                return `${collectionKey}[${indices[0]}].Alignment`;
            }
        } else {
            const record = toRecord(collection);
            if (record && 'Alignment' in record) {
                return `${collectionKey}.Alignment`;
            }
        }
    }

    const candidates: string[] = [];
    for (const [key, value] of Object.entries(root)) {
        const record = toRecord(value);
        if (record && 'Alignment' in record) {
            candidates.push(`${key}.Alignment`);
        }
    }

    return candidates.length === 1 ? candidates[0] : null;
}

export function getAllowedAlignmentsForKey(
    componentProps: unknown,
    alignKeyPath: string
): string[] {
    const containerPath = String(alignKeyPath).replace(
        /(^|\.|\[)Alignment(\]|\.|$)/,
        '$1'
    );

    const container =
        containerPath === ''
            ? componentProps
            : getByPath(componentProps, containerPath);
    const record = toRecord(container);

    const raw =
        record?.AlignmentAllowed ||
        record?.AllowedAlignments ||
        record?.AlignmentOptions ||
        record?.alignmentAllowed;

    const set = normSet(raw);
    return set.length ? set : ['left', 'center', 'right'];
}

export function readAlignmentAtPath(
    componentProps: unknown,
    path: string,
    allowed?: readonly string[]
): string {
    const raw = getByPath(componentProps, path);
    const record = toRecord(raw);
    const value = record && 'value' in record ? record.value : raw;
    return normalizeAlignment(value, allowed);
}
